import {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";

/**
 * True when `pathname` is already scoped to a workspace id (`/workspace/:canvasId`, capture, session, etc.).
 * Used to skip replaying chrome entry motion when switching between canvases or nested workspace routes.
 */
export function isNestedWorkspaceCanvasPath(pathname: string): boolean {
  return /^\/canvas\/[^/]+/.test(pathname);
}

const CanvasChromeEnterAnimationContext = createContext(true);

type CommittedSnapshot = { pathname: string; animate: boolean };

const initialCommittedSnapshot: CommittedSnapshot = {
  pathname: "",
  animate: true,
};

function computeShouldAnimateChromeEnter(prevPathname: string): boolean {
  return !isNestedWorkspaceCanvasPath(prevPathname);
}

/**
 * Decides whether canvas chrome should play its enter motion.
 *
 * We cannot read "history.location - 1" (or any prior stack entry): the History API and React Router's
 * `history` do not expose the previous pathname. Tracking the last pathname we committed while this
 * provider is mounted is equivalent for in-app navigations, and resets when leaving the canvas shell.
 *
 * The committed snapshot is updated only in `useLayoutEffect` so concurrent or discarded renders
 * cannot advance "previous pathname" and corrupt the next animation decision.
 */
export function CanvasChromeEnterAnimationProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { pathname } = useLocation();
  const committedSnapshotRef = useRef<CommittedSnapshot>(
    initialCommittedSnapshot,
  );

  const committed = committedSnapshotRef.current;
  const shouldAnimate =
    pathname === committed.pathname
      ? committed.animate
      : computeShouldAnimateChromeEnter(committed.pathname);

  useLayoutEffect(() => {
    const c = committedSnapshotRef.current;
    if (pathname === c.pathname) {
      return;
    }
    committedSnapshotRef.current = {
      pathname,
      animate: computeShouldAnimateChromeEnter(c.pathname),
    };
  }, [pathname]);

  return (
    <CanvasChromeEnterAnimationContext.Provider value={shouldAnimate}>
      {children}
    </CanvasChromeEnterAnimationContext.Provider>
  );
}

/**
 * Whether canvas shell chrome (toolbar, top bar, share) should play its entry motion for this navigation.
 * False when the previous URL was already under `/workspace/:canvasId` (e.g. switching canvases).
 */
export function useCanvasChromeEnterAnimation(): boolean {
  return useContext(CanvasChromeEnterAnimationContext);
}
